import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { products } from '@/mocks/products';
import { useCart } from '@/context/CartContext';
import ImageGallery from './components/ImageGallery';
import ProductAccordion from './components/ProductAccordion';
import RelatedProducts from './components/RelatedProducts';

const WHATSAPP_NUMBER = '91XXXXXXXXXX';

function buildSingleItemMessage(
  product: (typeof products)[number],
  variantLabel: string | undefined,
  variantPrice: number | undefined,
  quantity: number,
): string {
  const price = variantPrice ?? product.price;
  const lineTotal = price * quantity;
  const variantStr = variantLabel ? ` (${variantLabel})` : '';

  const message = [
    '🐾 *Quick Order from PounceBounce!*',
    '',
    `*${product.name}*${variantStr} x${quantity}`,
    `Subtotal: Rs.${lineTotal}`,
    '',
    '_Please confirm my order! 🐾_',
  ].join('\n');

  return message;
}

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { addItem, openCart } = useCart();

  const product = products.find(p => p.slug === slug);

  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(
    product?.variants?.[0]?.id,
  );
  const [quantity, setQuantity] = useState(1);
  const [addedFeedback, setAddedFeedback] = useState(false);

  if (!product) {
    return (
      <div className="bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-5">
            <i className="ri-footprint-line text-3xl text-foreground-300"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800 mb-2">Product Not Found</h1>
          <p className="text-foreground-500 text-sm mb-6">This toy seems to have bounced away. Let us help you find something your pet will love.</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors duration-200"
          >
            Browse All Products
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    );
  }

  const selectedVariant = product.variants?.find(v => v.id === selectedVariantId);
  const displayPrice = selectedVariant?.price ?? product.price;
  const displayMrp = selectedVariant?.mrp ?? product.mrp;

  const handleAddToCart = () => {
    addItem(product, quantity, selectedVariantId);
    setAddedFeedback(true);
    setTimeout(() => setAddedFeedback(false), 2000);
  };

  const handleBuyNowWhatsApp = () => {
    addItem(product, quantity, selectedVariantId);
    const message = buildSingleItemMessage(
      product,
      selectedVariant?.label,
      selectedVariant?.price,
      quantity,
    );
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
  };

  return (
    <div className="bg-background-50 min-h-screen">
      {/* Breadcrumb */}
      <div className="pt-24 pb-0 px-4 md:px-8 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4 flex-wrap">
            <Link to="/" className="hover:text-accent-500 transition-colors duration-150">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <Link to="/shop" className="hover:text-accent-500 transition-colors duration-150">Shop</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-600 truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product detail section */}
      <div className="px-4 md:px-8 lg:px-12 pb-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left: Image Gallery */}
            <div className="w-full lg:w-7/12 xl:w-3/5">
              <ImageGallery images={product.images} productName={product.name} />
            </div>

            {/* Right: Product Info */}
            <div className="w-full lg:w-5/12 xl:w-2/5">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                  product.petType === 'dog'
                    ? 'bg-primary-50 text-primary-600'
                    : product.petType === 'cat'
                    ? 'bg-accent-50 text-accent-600'
                    : 'bg-secondary-200 text-foreground-600'
                }`}>
                  {product.petType === 'dog' ? 'For Dogs' : product.petType === 'cat' ? 'For Cats' : 'Universal'}
                </span>
                <span className="px-2.5 py-1 bg-secondary-200/70 text-foreground-600 rounded-full text-xs font-medium">
                  {product.functionCategory}
                </span>
                {product.isNew && (
                  <span className="px-2.5 py-1 bg-primary-500 text-background-50 rounded-full text-xs font-bold uppercase tracking-wider">
                    NEW
                  </span>
                )}
                {product.isBestseller && (
                  <span className="px-2.5 py-1 bg-[#D9A441] text-background-50 rounded-full text-xs font-bold uppercase tracking-wider">
                    BESTSELLER
                  </span>
                )}
              </div>

              {/* Name */}
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-2">
                {product.name}
              </h1>

              {/* Tagline */}
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-4">
                {product.tagline}
              </p>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-5">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-sm ${i < Math.floor(product.rating) ? 'ri-star-fill text-[#D9A441]' : 'ri-star-line text-foreground-300'}`}></i>
                    </span>
                  ))}
                </div>
                <span className="text-sm font-medium text-foreground-700">{product.rating}</span>
                <span className="text-xs text-foreground-400">({product.reviewCount} reviews)</span>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-2xl md:text-3xl font-bold text-foreground-900">Rs.{displayPrice}</span>
                {displayMrp && displayMrp > displayPrice && (
                  <span className="text-sm text-foreground-400 line-through">Rs.{displayMrp}</span>
                )}
                {displayMrp && displayMrp > displayPrice && (
                  <span className="px-2 py-0.5 bg-accent-100 text-accent-600 rounded-full text-xs font-bold">
                    Save Rs.{displayMrp - displayPrice}
                  </span>
                )}
              </div>

              {/* Variant Selector */}
              {product.variants && product.variants.length > 0 && (
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-foreground-700 uppercase tracking-wider mb-2.5">
                    Size
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map(variant => (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariantId(variant.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          selectedVariantId === variant.id
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200 border border-transparent hover:border-accent-300'
                        }`}
                      >
                        {variant.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity + Add to Cart */}
              <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-4">
                {/* Quantity stepper */}
                <div className="flex items-center gap-0 bg-secondary-100 rounded-full p-1 flex-shrink-0">
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-50 transition-colors duration-150 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Decrease quantity"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-10 text-center text-sm font-bold text-foreground-800">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => q + 1)}
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-50 transition-colors duration-150 cursor-pointer"
                    aria-label="Increase quantity"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>

                {/* Add to Cart button */}
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap paw-bounce ${
                    addedFeedback
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    {addedFeedback ? (
                      <i className="ri-check-line paw-icon"></i>
                    ) : (
                      <i className="ri-shopping-bag-line paw-icon"></i>
                    )}
                  </span>
                  {addedFeedback ? 'Added to Cart!' : 'Add to Cart'}
                </button>
              </div>

              {/* Buy Now via WhatsApp */}
              <button
                onClick={handleBuyNowWhatsApp}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap bg-[#25D366] text-white hover:bg-[#22c35e] paw-bounce mb-6"
              >
                <i className="ri-whatsapp-fill text-lg paw-icon"></i>
                Buy Now via WhatsApp
              </button>

              {/* Material highlights */}
              <div className="bg-secondary-100/80 rounded-xl p-4 mb-6">
                <h3 className="text-xs font-bold text-foreground-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-leaf-line text-primary-500"></i>
                  </span>
                  Material Highlights
                </h3>
                <ul className="space-y-1.5">
                  {product.materialClaims.slice(0, 4).map((claim, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-foreground-600">
                      <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-footprint-line text-accent-500 text-[10px]"></i>
                      </span>
                      {claim}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Description */}
              <p className="text-sm text-foreground-500 leading-relaxed">
                {product.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion section */}
      <div className="px-4 md:px-8 lg:px-12 pb-8">
        <div className="max-w-screen-2xl mx-auto">
          <ProductAccordion product={product} />
        </div>
      </div>

      {/* Related Products */}
      <div className="px-4 md:px-8 lg:px-12 pb-16">
        <div className="max-w-screen-2xl mx-auto">
          <RelatedProducts currentProduct={product} />
        </div>
      </div>
    </div>
  );
}